import { IconButton, InputAdornment, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { CloseSmallIcon, SearchIcon } from "src/foundations/icons";
import { SenecTextField } from "src/components/inputs/input-field/SenecTextField";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";

export interface SenecSearchbarProps {
    /**
     * Can be used to disable changes on the input field when the site is loading
     */
    readOnly?: boolean;
    /**
     * Can be used to pass the name or action of the field e.g. "Search"
     */
    searchPlaceholder?: string;
    /**
     * (optional) to pass an initial field value
     */
    initialSearchbarValue?: string;
    /**
     * Defines the input field size. Alternatives: "small" or "medium"
     */
    size?: "small" | "medium";
    /**
     * (optional) defines maximal number of input characters, no limit by default
     */
    maxLength?: number;
    /**
     * (optional) accepts a function with the searchTerm as parameter
     * @param searchTerm
     */
    onSearch?: (searchTerm: string) => void;

    /**
     * (optional) accepts a function with the searchTerm as parameter
     * @param searchTerm
     */
    onChange?: (searchTerm: string) => void;
    /**
     * (optional) accepts a function
     */
    onClear?: () => void;
    /**
     * Variant of textfield "outlined" or "filled"
     */
    variant?: "outlined" | "filled";
}

/**
 * `SenecSearchbar` is used to execute a search of an input term.
 */
export const SenecSearchbar = ({
    readOnly = false,
    onSearch,
    searchPlaceholder = "",
    initialSearchbarValue = "",
    size = "medium",
    maxLength,
    onClear,
    variant = "filled",
    onChange,
}: SenecSearchbarProps) => {
    const [searchbarValue, setSearchbarValue] = useState<string>(initialSearchbarValue);
    const theme: SenecBaseTheme = useTheme();

    useEffect(() => {
        setSearchbarValue(initialSearchbarValue);
    }, [initialSearchbarValue]);

    const handleClear = () => {
        setSearchbarValue("");
        if (onChange) {
            onChange("");
        }
        if (onClear) return onClear();
    };

    const handleSearch = () => {
        if (searchbarValue !== "") {
            if (onSearch) onSearch(searchbarValue);
        }
    };

    const senecSearchbarFontStyle = size === "small" ? theme.typography.enbwTabletS1 : theme.typography.enbwTabletP1;

    return (
        <SenecTextField
            role="search"
            variant={variant}
            disabled={readOnly}
            size={size}
            value={searchbarValue}
            placeholder={searchPlaceholder}
            hiddenLabel
            onChange={(e) => {
                const value = e.target.value ? e.target.value : "";
                setSearchbarValue(value);
                if (onChange) {
                    onChange(value);
                }
            }}
            onKeyDown={(e) => {
                if (e.key == "Enter" && onSearch) {
                    onSearch((e.target as HTMLInputElement).value);
                }
            }}
            sx={{ "& .MuiInputBase-root.MuiOutlinedInput-root": { paddingRight: size === "small" ? 0 : "8px" } }}
            inputProps={{ maxLength: maxLength }}
            InputProps={{
                readOnly: readOnly,
                sx: {
                    input: {
                        ":placeholder-shown": {
                            textOverflow: "ellipsis",
                        },
                        paddingLeft: "0.75rem",
                        ...senecSearchbarFontStyle,
                    },
                },
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="Clear"
                            data-testid={"clear-button"}
                            style={{
                                display: searchbarValue ? "inline-flex" : "none",
                            }}
                            disabled={readOnly}
                            onClick={() => handleClear()}
                        >
                            <CloseSmallIcon fontSize="medium" sx={{ p: 0, color: "senecSecondary.neutralGray40" }} />
                        </IconButton>
                        <IconButton
                            data-testid={"search-button"}
                            disabled={readOnly}
                            onClick={() => {
                                handleSearch();
                            }}
                        >
                            <SearchIcon fontSize="medium" sx={{ p: 0, color: "text.primary" }} />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
};
